import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  FoodCategory,
  GetFoodCategoriesDocument,
  GetFoodCategoriesQuery,
  useCreateFoodCategoryMutation,
} from '../../../../generated/graphql';
import { ErrorText } from '../../../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export type FormValues = {
  name: string;
  subtitle?: string;
  is_active: boolean;
};

export const schema = yup
  .object({
    name: yup.string().required(ErrorText.Required),
    is_active: yup.boolean().required(ErrorText.Required),
  })
  .required();

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: CreateProps) {
  const [createFoodCategory, { loading: createLoading }] =
    useCreateFoodCategoryMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createFoodCategory({
      variables: { input: entry },
      update: (cache, { data }) => {
        const addData = data?.createFoodCategory;
        const existingData = cache.readQuery({
          query: GetFoodCategoriesDocument,
        }) as GetFoodCategoriesQuery;
        const oldData = existingData.listFoodCategories
          ?.items as FoodCategory[];

        cache.writeQuery({
          query: GetFoodCategoriesDocument,
          data: {
            listFoodCategories: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createFoodCategory;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.name}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form />
      </Modal>
    </FormProvider>
  );
}
